/**
 * MembersPanel — Admin CRUD for club_members under /admin shell.
 *
 * FIR-20260612-006 · /admin previously had no Members tab; the only
 * surface was /club/admin/members under the club portal. Founder asked
 * for a tab on /admin matching the Clubs/Venues catalogue pattern.
 *
 * Lean parity panel: list · search · filter (tenant/status/authorised)
 * · add · edit · delete · bulk authorise · CSV import · CSV export.
 * Reuses the same /api/club/admin/members* endpoints already wired —
 * those endpoints honour the /admin Bearer JWT, not just /mira/console.
 */

import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { resizeImageForUpload } from "@/lib/imageResize";

const TENANT_OPTIONS = ["", "global", "kvb", "lesconcierges"];
const STATUS_OPTIONS = ["", "active", "inactive", "pending_review"];

export default function MembersPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // Filters
  const [q, setQ] = useState("");
  const [tenant, setTenant] = useState("");
  const [status, setStatus] = useState("");
  const [authorised, setAuthorised] = useState("");

  // Selection (for bulk)
  const [selected, setSelected] = useState(new Set());

  // Drawer (add/edit)
  const [editing, setEditing] = useState(null);  // null = closed; "new" or member object
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q) params.q = q;
      if (tenant) params.tenant = tenant;
      if (status) params.status = status;
      if (authorised !== "") params.authorised = authorised;
      const { data } = await api.get("/club/admin/members", { params });
      setItems(data.items || []);
      setTotal(data.total ?? (data.items || []).length);
      setSelected(new Set());
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e?.response?.data?.detail) || e.message });
    } finally {
      setLoading(false);
    }
  }, [q, tenant, status, authorised]);

  useEffect(() => { loadList(); }, [loadList]);

  function resetFilters() {
    setQ(""); setTenant(""); setStatus(""); setAuthorised("");
  }

  function toggleSel(card) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(card)) n.delete(card); else n.add(card);
      return n;
    });
  }

  async function bulkAuth(toState) {
    if (selected.size === 0) return;
    try {
      const { data } = await api.post("/club/admin/members/bulk-authorise", {
        cards: Array.from(selected),
        authorised: toState,
      });
      setMsg({ ok: true, text: `${toState ? "Authorised" : "De-authorised"} ${data.modified || 0} member(s)` });
      await loadList();
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e?.response?.data?.detail) || e.message });
    }
  }

  function openNew() {
    setEditing("new");
    setDraft({ tenant: "lesconcierges", authorised: false, member_status: "active" });
  }
  function openEdit(m) {
    setEditing(m);
    setDraft({ ...m });
  }
  function closeDrawer() {
    setEditing(null); setDraft({}); setSaving(false);
  }

  async function saveDraft() {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/club/admin/members", draft);
        setMsg({ ok: true, text: `Member ${draft.membership_card_no} created` });
      } else {
        // PATCH only changed fields — for simplicity send the whole draft
        const patch = { ...draft };
        delete patch.membership_card_no; // card_no is the immutable key
        await api.patch(`/club/admin/members/${editing.membership_card_no || editing.card_no}`, patch);
        setMsg({ ok: true, text: "Member updated" });
      }
      closeDrawer();
      await loadList();
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e?.response?.data?.detail) || e.message });
    } finally {
      setSaving(false);
    }
  }

  async function deleteMember(m) {
    const card = m.membership_card_no || m.card_no;
    if (!window.confirm(`Delete member ${card}? This cannot be undone.`)) return;
    try {
      await api.delete(`/club/admin/members/${card}?hard=true`);
      setMsg({ ok: true, text: `Deleted ${card}` });
      await loadList();
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e?.response?.data?.detail) || e.message });
    }
  }

  async function exportCsv() {
    try {
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      if (tenant) qs.set("tenant", tenant);
      if (status) qs.set("status", status);
      if (authorised !== "") qs.set("authorised", authorised);
      const res = await api.get(`/club/admin/members/export-csv?${qs.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `members-${ts}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setMsg({ ok: true, text: `Export downloaded · ${total.toLocaleString()} member(s)` });
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e?.response?.data?.detail) || e.message });
    }
  }

  async function importCsv(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/club/admin/members/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg({
        ok: true,
        text: `Imported · added ${data.added} · updated ${data.updated} · errors ${data.errors}`,
      });
      await loadList();
    } catch (e2) {
      setMsg({ ok: false, text: formatApiError(e2?.response?.data?.detail) || e2.message });
    } finally {
      e.target.value = "";
    }
  }

  // FIR-20260612-009 · Issue real card number to a PENDING-XXXX self-signup.
  // Opens a small modal · POST /members/{old}/issue-card · then refreshes.
  const [issueTarget, setIssueTarget] = useState(null);  // null | member object
  const [issueNew, setIssueNew] = useState("");
  const [issueAuth, setIssueAuth] = useState(true);
  const [issueSaving, setIssueSaving] = useState(false);

  async function submitIssueCard() {
    const oldCard = issueTarget.membership_card_no || issueTarget.card_no;
    if (!issueNew.trim()) {
      setMsg({ ok: false, text: "Please enter a card number." });
      return;
    }
    setIssueSaving(true);
    try {
      const { data } = await api.post(`/club/admin/members/${oldCard}/issue-card`, {
        new_card_no: issueNew.trim(),
        authorise: issueAuth,
      });
      setMsg({ ok: true, text: `Card ${data.new_card_no} issued${data.authorised ? " · authorised" : ""}. You can now send the welcome email.` });
      setIssueTarget(null); setIssueNew(""); setIssueAuth(true);
      await loadList();
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e?.response?.data?.detail) || e.message });
    } finally {
      setIssueSaving(false);
    }
  }

  // Send welcome email to an authorised member (proxy to existing endpoint).
  async function sendWelcome(m) {
    const card = m.membership_card_no || m.card_no;
    if (!window.confirm(`Send welcome email to ${m.email || card}?`)) return;
    try {
      await api.post(`/club/admin/members/${card}/welcome`);
      setMsg({ ok: true, text: `Welcome email sent to ${m.email}` });
      await loadList();
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e?.response?.data?.detail) || e.message });
    }
  }

  return (
    <div className="space-y-8" data-testid="members-panel">
      <header>
        <p className="eyebrow">Members · club_members</p>
        <h2 className="font-serif text-3xl md:text-4xl mt-2">Members catalogue</h2>
        <p className="mt-3 text-[14px] text-[hsl(var(--muted))] max-w-2xl">
          Add and edit members across every tenant. Bulk-authorise · CSV import · CSV export.
          Click any row to open the edit drawer.
        </p>
      </header>

      {msg && (
        <div className={`p-3 rounded-md text-[13px] ${msg.ok ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`} data-testid="members-msg">
          {msg.text}
        </div>
      )}

      {/* Top action bar */}
      <div className="flex gap-2 items-center flex-wrap">
        <input
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email or card…"
          data-testid="members-search"
          className="px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.3)] bg-white text-[14px] flex-1 min-w-[200px]"
        />
        <select value={tenant} onChange={(e) => setTenant(e.target.value)} data-testid="members-filter-tenant"
                className="px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.3)] bg-white text-[14px]">
          {TENANT_OPTIONS.map((t) => <option key={t} value={t}>{t || "All tenants"}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="members-filter-status"
                className="px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.3)] bg-white text-[14px]">
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <select value={authorised} onChange={(e) => setAuthorised(e.target.value)} data-testid="members-filter-authorised"
                className="px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.3)] bg-white text-[14px]">
          <option value="">All authorisation</option>
          <option value="true">Authorised</option>
          <option value="false">Pending</option>
        </select>
        <button onClick={resetFilters} data-testid="members-reset"
                className="text-[11px] tracking-[0.14em] uppercase px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.5)] text-[hsl(var(--navy))] hover:bg-[hsl(var(--brass)/0.10)]">
          Reset
        </button>
        <button onClick={exportCsv} data-testid="members-export-button"
                className="text-[11px] tracking-[0.14em] uppercase px-3 py-2 rounded-md bg-[hsl(var(--navy))] text-white hover:bg-[hsl(var(--navy)/0.85)]">
          Export CSV
        </button>
        <label className="text-[11px] tracking-[0.14em] uppercase px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.5)] text-[hsl(var(--navy))] hover:bg-[hsl(var(--brass)/0.10)] cursor-pointer">
          Import CSV
          <input type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="members-import-file" />
        </label>
        <button onClick={openNew} data-testid="members-add-button"
                className="text-[11px] tracking-[0.14em] uppercase px-3 py-2 rounded-md bg-[hsl(var(--brass))] text-white hover:bg-[hsl(var(--brass)/0.85)]">
          + Add member
        </button>
      </div>

      {/* Selection bar (only when something is selected) */}
      {selected.size > 0 && (
        <div className="flex gap-2 items-center p-3 rounded-md bg-amber-50 text-amber-900 text-[13px]" data-testid="members-selection-bar">
          {selected.size} selected ·
          <button onClick={() => bulkAuth(true)} className="underline font-medium" data-testid="members-bulk-auth">
            Authorise all
          </button>
          ·
          <button onClick={() => bulkAuth(false)} className="underline font-medium" data-testid="members-bulk-deauth">
            De-authorise all
          </button>
          ·
          <button onClick={() => setSelected(new Set())} className="underline">Clear</button>
        </div>
      )}

      {/* List */}
      <div className="rounded-md border border-[hsl(var(--brass)/0.25)] overflow-hidden" data-testid="members-table-wrap">
        <div className="px-4 py-2 bg-[hsl(var(--brass)/0.05)] text-[12px] tracking-[0.18em] uppercase text-[hsl(var(--muted))]">
          {loading ? "Loading…" : `${total.toLocaleString()} member${total === 1 ? "" : "s"}`}
        </div>
        <table className="w-full text-[13px]">
          <thead className="bg-white border-b border-[hsl(var(--brass)/0.2)]">
            <tr className="text-left">
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2">Card</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Tenant</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Auth</th>
              <th className="px-3 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => {
              const card = m.membership_card_no || m.card_no;
              return (
                <tr key={card} className="border-t border-[hsl(var(--brass)/0.10)] hover:bg-[hsl(var(--brass)/0.04)]" data-testid={`members-row-${card}`}>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={selected.has(card)} onChange={() => toggleSel(card)} data-testid={`members-select-${card}`} />
                  </td>
                  <td className="px-3 py-2 font-mono text-[12px]">{card}</td>
                  <td className="px-3 py-2 cursor-pointer" onClick={() => openEdit(m)}>
                    {(m.first_name || m.first || "") + " " + (m.last_name || m.last || "")}
                  </td>
                  <td className="px-3 py-2">{m.email || ""}</td>
                  <td className="px-3 py-2">{m.tenant || ""}</td>
                  <td className="px-3 py-2">{m.member_status || m.status || ""}</td>
                  <td className="px-3 py-2">
                    {m.authorised ? (
                      <span className="text-emerald-700">✓</span>
                    ) : (
                      <span className="text-amber-700">pending</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {(card || "").startsWith("PENDING-") && (
                      <button onClick={() => { setIssueTarget(m); setIssueNew(""); setIssueAuth(true); }}
                              className="text-[11px] text-[hsl(var(--brass))] hover:underline mr-3"
                              data-testid={`members-issue-card-${card}`}>
                        issue card
                      </button>
                    )}
                    {m.authorised && m.email && !(card || "").startsWith("PENDING-") && (
                      <button onClick={() => sendWelcome(m)}
                              className="text-[11px] text-[hsl(var(--navy))] hover:underline mr-3"
                              data-testid={`members-welcome-${card}`}>
                        {m.welcome_email_sent_at ? "resend welcome" : "send welcome"}
                      </button>
                    )}
                    <button onClick={() => deleteMember(m)} className="text-[11px] text-rose-700 hover:underline" data-testid={`members-delete-${card}`}>delete</button>
                  </td>
                </tr>
              );
            })}
            {!loading && items.length === 0 && (
              <tr><td colSpan="8" className="px-3 py-6 text-center text-[hsl(var(--muted))]">No members match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center md:justify-center p-4" onClick={closeDrawer} data-testid="members-drawer">
          <div className="bg-white rounded-md max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-4">
              {editing === "new" ? "Add member" : `Edit ${draft.first_name || draft.first} ${draft.last_name || draft.last}`}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
              {editing === "new" && (
                <FormField label="Membership card no *" value={draft.membership_card_no || ""} onChange={(v) => setDraft({ ...draft, membership_card_no: v })} testid="members-form-card-no" />
              )}
              <FormField label="First name *" value={draft.first_name || draft.first || ""} onChange={(v) => setDraft({ ...draft, first_name: v })} testid="members-form-first-name" />
              <FormField label="Last name *" value={draft.last_name || draft.last || ""} onChange={(v) => setDraft({ ...draft, last_name: v })} testid="members-form-last-name" />
              <FormField label="Email" value={draft.email || ""} onChange={(v) => setDraft({ ...draft, email: v })} testid="members-form-email" />
              <FormField label="Mobile" value={draft.mobile || draft.phone || ""} onChange={(v) => setDraft({ ...draft, mobile: v })} testid="members-form-mobile" />
              <FormField label="Resident city" value={draft.resident_city || ""} onChange={(v) => setDraft({ ...draft, resident_city: v })} testid="members-form-city" />
              <FormField label="Resident state" value={draft.resident_state || ""} onChange={(v) => setDraft({ ...draft, resident_state: v })} testid="members-form-state" />
              <FormField label="Spouse name" value={draft.spouse_name || ""} onChange={(v) => setDraft({ ...draft, spouse_name: v })} testid="members-form-spouse" />
              <FormSelect label="Tenant" value={draft.tenant || ""} options={TENANT_OPTIONS.filter(Boolean)} onChange={(v) => setDraft({ ...draft, tenant: v })} testid="members-form-tenant" />
              <FormSelect label="Status" value={draft.member_status || draft.status || ""} options={STATUS_OPTIONS.filter(Boolean)} onChange={(v) => setDraft({ ...draft, member_status: v })} testid="members-form-status" />
              <label className="flex items-center gap-2 col-span-2">
                <input type="checkbox" checked={!!draft.authorised} onChange={(e) => setDraft({ ...draft, authorised: e.target.checked })} data-testid="members-form-authorised" />
                <span>Authorised (can sign in via /club gate)</span>
              </label>
            </div>

            {/* FIR-20260612-011 · member + spouse photo uploads. Only
                 available after Save (we need a card_no for the storage
                 path). Stored in Cloudinary folder lc/members/{card_no}
                 with `slot=self|spouse`. The member card eventually
                 prints from `photo_url`. */}
            {editing !== "new" && (editing.membership_card_no || editing.card_no) && (
              <div className="mt-5 pt-4 border-t border-[hsl(var(--brass)/0.2)]">
                <p className="text-[11px] tracking-[0.14em] uppercase text-[hsl(var(--muted))] mb-2">Photos</p>
                <div className="flex gap-4">
                  <MemberPhoto
                    card={editing.membership_card_no || editing.card_no}
                    slot="self"
                    label="Member photo (as per Aadhar)"
                    url={draft.photo_url}
                    onUploaded={(url) => setDraft((d) => ({ ...d, photo_url: url }))}
                    setErr={(text) => setMsg({ ok: false, text })}
                  />
                  <MemberPhoto
                    card={editing.membership_card_no || editing.card_no}
                    slot="spouse"
                    label="Spouse photo (as per Aadhar)"
                    url={draft.spouse_photo_url}
                    onUploaded={(url) => setDraft((d) => ({ ...d, spouse_photo_url: url }))}
                    setErr={(text) => setMsg({ ok: false, text })}
                  />
                </div>
                <p className="mt-2 text-[10px]" style={{ color: "var(--muted)" }}>
                  Stored in Cloudinary · the member card prints from these images.
                </p>
              </div>
            )}
            {editing === "new" && (
              <p className="mt-3 text-[11px] italic" style={{ color: "hsl(var(--muted))" }}>
                Save the member first, then re-open the row to upload photos.
              </p>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={closeDrawer} className="text-[11px] tracking-[0.14em] uppercase px-3 py-2 border border-[hsl(var(--brass)/0.5)] rounded-md" data-testid="members-form-cancel">
                Cancel
              </button>
              <button onClick={saveDraft} disabled={saving} className="text-[11px] tracking-[0.14em] uppercase px-3 py-2 bg-[hsl(var(--brass))] text-white rounded-md disabled:opacity-50" data-testid="members-form-save">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Issue card · for PENDING- self-signups */}
      {issueTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setIssueTarget(null)} data-testid="members-issue-drawer">
          <div className="bg-white rounded-md max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-2">Issue membership card</h3>
            <p className="text-[12px] text-[hsl(var(--muted))] mb-4">
              Replacing <span className="font-mono">{issueTarget.membership_card_no || issueTarget.card_no}</span>
              {" "}for <strong>{(issueTarget.first_name || issueTarget.first) + " " + (issueTarget.last_name || issueTarget.last)}</strong>
              {issueTarget.email ? <> · {issueTarget.email}</> : null}.
            </p>
            <label className="block mb-4">
              <span className="block text-[11px] tracking-[0.14em] uppercase text-[hsl(var(--muted))] mb-1">New card number</span>
              <input type="text" value={issueNew} onChange={(e) => setIssueNew(e.target.value)}
                     placeholder="e.g. LC-2026-0042 or KVB-1037"
                     autoFocus
                     data-testid="members-issue-card-input"
                     className="w-full px-3 py-2 rounded border border-[hsl(var(--brass)/0.3)] bg-white text-[14px] font-mono" />
            </label>
            <label className="flex items-center gap-2 text-[13px] mb-5">
              <input type="checkbox" checked={issueAuth} onChange={(e) => setIssueAuth(e.target.checked)} data-testid="members-issue-auth" />
              <span>Also authorise this member to sign in immediately</span>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setIssueTarget(null)} className="text-[11px] tracking-[0.14em] uppercase px-3 py-2 border border-[hsl(var(--brass)/0.5)] rounded-md" data-testid="members-issue-cancel">Cancel</button>
              <button onClick={submitIssueCard} disabled={issueSaving} className="text-[11px] tracking-[0.14em] uppercase px-3 py-2 bg-[hsl(var(--brass))] text-white rounded-md disabled:opacity-50" data-testid="members-issue-confirm">
                {issueSaving ? "Issuing…" : "Issue card"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.14em] uppercase text-[hsl(var(--muted))] mb-1">{label}</span>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-2 py-1.5 rounded border border-[hsl(var(--brass)/0.3)] bg-white text-[13px]"
      />
    </label>
  );
}

function FormSelect({ label, value, options, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.14em] uppercase text-[hsl(var(--muted))] mb-1">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
              className="w-full px-2 py-1.5 rounded border border-[hsl(var(--brass)/0.3)] bg-white text-[13px]">
        <option value=""></option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function MemberPhoto({ card, slot, label, url, onUploaded, setErr }) {
  const [uploading, setUploading] = React.useState(false);

  async function upload(e) {
    const raw = e.target.files?.[0];
    if (!raw) return;
    setUploading(true);
    try {
      const file = await resizeImageForUpload(raw);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/club/admin/members/${card}/photo?slot=${slot}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const newUrl = data?.url || data?.member?.[slot === "self" ? "photo_url" : "spouse_photo_url"];
      if (newUrl) onUploaded(newUrl);
    } catch (err) {
      setErr(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  return (
    <div className="flex flex-col items-start" style={{ minWidth: 140 }}>
      <div style={{
        width: 120, height: 150, borderRadius: 6, overflow: "hidden",
        border: "1px solid hsl(var(--brass) / 0.3)",
        background: "rgba(35,48,42,0.04)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {url ? (
          <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span style={{ fontSize: 10, color: "var(--muted)" }}>no photo</span>
        )}
      </div>
      <label className="mt-2 text-[10px] tracking-[0.14em] uppercase px-2 py-1 rounded-md border border-[hsl(var(--brass)/0.5)] text-[hsl(var(--navy))] hover:bg-[hsl(var(--brass)/0.10)] cursor-pointer">
        {uploading ? "Uploading…" : url ? "Replace" : "Upload"}
        <input type="file" accept="image/*" className="hidden"
               data-testid={`member-photo-upload-${slot}`} onChange={upload} disabled={uploading} />
      </label>
      <span className="mt-1 text-[10px]" style={{ color: "var(--muted)" }}>{label}</span>
    </div>
  );
}
